import { useState } from "react";
import i18next from "i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Avatar from "@mui/material/Avatar";
import ShareIcon from "@mui/icons-material/Share";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LanguageIcon from "@mui/icons-material/Language";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TextField from "../../../../components/Form/TextField";
import CardContainer from "../../../../components/Card/CardContainer";
import SectionHeader from "./SectionHeader";
import {
  SECTION_SOCIAL_TITLE,
  SECTION_SOCIAL_SUBTITLE_SINGULAR,
  SECTION_SOCIAL_SUBTITLE_PLURAL,
} from "../../../../i18n/keysTranslations";
import { BORDER_RADIUS } from "../../../../utils/constants";
import {
  extractHandleFromSocialUrl,
  getSocialPlatformFromUrl,
} from "../../../../utils/socialHandle";
import { logoPlatform } from "../../../../utils/string";

export const SocialSection = ({ formMethods, hasError = false }) => {
  const [isEditing, setIsEditing] = useState(false);

  if (!formMethods || !formMethods.control) {
    return null;
  }

  const {
    control,
    errors,
    watch,
    setValue,
    socialNetworksFields,
    appendSocialNetworks,
    removeSocialNetworks,
    validationRules,
  } = formMethods;

  const socialNetworks = watch("socialNetworks") || [];
  const newUrl = watch("newSocialUrl") || "";

  const addSocialMedia = () => {
    const trimmedUrl = newUrl?.trim() || "";
    if (trimmedUrl && !socialNetworks.includes(trimmedUrl)) {
      appendSocialNetworks(trimmedUrl);
      setValue("newSocialUrl", "");
    }
  };

  const removeSocialMedia = (index) => {
    removeSocialNetworks(index);
  };

  const onSave = () => {
    setIsEditing(false);
  };

  return (
    <CardContainer title="" hasError={hasError}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Header */}
        <SectionHeader
          icon={ShareIcon}
          title={SECTION_SOCIAL_TITLE}
          subtitleCount={socialNetworksFields.length}
          subtitleCountKeySingular={SECTION_SOCIAL_SUBTITLE_SINGULAR}
          subtitleCountKeyPlural={SECTION_SOCIAL_SUBTITLE_PLURAL}
          isEditing={isEditing}
          onEdit={() => setIsEditing(true)}
          onSave={onSave}
        />

        {/* Content */}
        {isEditing ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Existing Social Media */}
            {socialNetworksFields.map((field, index) => (
              <Box
                key={field.id}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <TextField
                  control={control}
                  name={`socialNetworks.${index}`}
                  rules={validationRules.socialNetworks}
                  errors={errors}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-input": {
                      height: "40px",
                    },
                  }}
                  variant="filled"
                />
                <IconButton
                  size="small"
                  onClick={() => removeSocialMedia(index)}
                  sx={{
                    width: 40,
                    height: 40,
                    color: "text.primary",
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            ))}

            {/* Add New Social Media */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                control={control}
                name="newSocialUrl"
                placeholder="https://..."
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    addSocialMedia();
                  }
                }}
                variant="filled"
                sx={{
                  "& .MuiInputBase-input": {
                    height: "40px",
                  },
                }}
                rules={{ required: false }}
              />
              <IconButton
                size="small"
                onClick={addSocialMedia}
                disabled={
                  !newUrl?.trim() || socialNetworks.includes(newUrl?.trim())
                }
                sx={{
                  width: 40,
                  height: 40,
                  color: "text.primary",
                  flexShrink: 0,
                  "&.Mui-disabled": {
                    opacity: 0.5,
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {socialNetworks.map((url, index) => {
              const handle = extractHandleFromSocialUrl(url);
              const platform = getSocialPlatformFromUrl(url);
              const logo = platform ? logoPlatform(platform) : null;
              const displayText =
                handle || (platform ? i18next.t(platform) : url);

              return (
                <Link
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    maxWidth: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    backgroundColor: "#F5F5F5",
                    borderRadius: BORDER_RADIUS,
                    px: 2,
                    py: 1.5,
                    textDecoration: "none",
                    color: "text.primary",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: "#E5E5E5",
                      "& .external-link-icon": {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  {logo ? (
                    <Avatar
                      src={logo}
                      variant="square"
                      sx={{
                        width: 20,
                        height: 20,
                        flexShrink: 0,
                        ".MuiAvatar-img": {
                          objectFit: "contain",
                        },
                      }}
                    />
                  ) : (
                    <LanguageIcon
                      sx={{
                        width: 20,
                        height: 20,
                        flexShrink: 0,
                        color: "text.primary",
                      }}
                    />
                  )}

                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "text.primary",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {displayText}
                  </Typography>
                  <OpenInNewIcon
                    className="external-link-icon"
                    sx={{
                      width: 12,
                      height: 12,
                      flexShrink: 0,
                      color: "text.secondary",
                      opacity: 0,
                      transition: "opacity 0.2s",
                    }}
                  />
                </Link>
              );
            })}
          </Box>
        )}
      </Box>
    </CardContainer>
  );
};

export default SocialSection;
